import { useId, useState, type ReactNode } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

interface DisclosureProps {
    title: ReactNode;
    children: ReactNode;
    /** Initial state when uncontrolled */
    defaultExpanded?: boolean;
    /** Controlled state; pair with onToggle */
    expanded?: boolean;
    onToggle?: (expanded: boolean) => void;
    /** Rendered next to the title, e.g. a count pill */
    badge?: ReactNode;
    /** Rendered on the right of the header, before the chevron */
    headerActions?: ReactNode;
    disabled?: boolean;
    /** card: bordered zinc-900 container; plain: bare header button and content */
    variant?: 'card' | 'plain';
    className?: string;
}

/**
 * Collapsible section with a consistent chevron header and aria wiring.
 * Supports both uncontrolled (defaultExpanded) and controlled (expanded +
 * onToggle) usage.
 */
const Disclosure = ({
    title,
    children,
    defaultExpanded = false,
    expanded,
    onToggle,
    badge,
    headerActions,
    disabled = false,
    variant = 'card',
    className = ''
}: DisclosureProps) => {
    const contentId = useId();
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const isExpanded = expanded ?? internalExpanded;

    const handleToggle = () => {
        if (expanded === undefined) {
            setInternalExpanded(!isExpanded);
        }

        onToggle?.(!isExpanded);
    };

    const isCard = variant === 'card';

    return (
        <div className={`${isCard ? 'rounded-lg border border-zinc-700 bg-zinc-900' : ''} ${className}`}>
            <div className={`flex w-full items-center ${isCard ? 'px-4 py-3' : 'py-1'}`}>
                <button
                    aria-controls={contentId}
                    aria-expanded={isExpanded}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm font-medium text-zinc-200 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={disabled}
                    type="button"
                    onClick={handleToggle}
                >
                    <span className="truncate">{title}</span>
                    {badge}
                </button>
                {headerActions && <span className="ml-2 flex shrink-0 items-center gap-2">{headerActions}</span>}
                <button
                    aria-controls={contentId}
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
                    className="ml-2 shrink-0 rounded-md p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={disabled}
                    type="button"
                    onClick={handleToggle}
                >
                    {isExpanded ? <FaChevronUp className="h-4 w-4" /> : <FaChevronDown className="h-4 w-4" />}
                </button>
            </div>
            {isExpanded && (
                <div className={isCard ? 'border-t border-zinc-700 p-4' : 'pt-1'} id={contentId}>
                    {children}
                </div>
            )}
        </div>
    );
};

export default Disclosure;
